import { NullAnalyticsService } from './null-analytics-service';
import type {
  AnalyticsManager,
  AnalyticsService,
  CreateAnalyticsManagerOptions,
} from './types';

/**
 * Lazy-Init Analytics-Manager.
 *
 * Vorher: registerActiveServices() lief als Modul-Top-Level beim Import →
 * service.initialize() startete PostHog UNABHÄNGIG vom Cookie-Consent →
 * TTDSG § 25-Verstoß weil PostHog-Cookies vor User-Einwilligung gesetzt wurden.
 *
 * Nachher: Manager registriert nur Provider-Factories. service.initialize()
 * läuft erst beim ersten enableServices()-Call – triggert vom CookieYes-
 * Consent-Sync-Effect im AnalyticsProvider.
 */
export function createAnalyticsManager<T extends string, Config extends object>(
  options: CreateAnalyticsManagerOptions<T, Config>,
): AnalyticsManager {
  const factories = new Map<string, () => AnalyticsService>();
  const activeServices = new Map<string, AnalyticsService>();

  Object.entries(options.providers).forEach(([key, factory]) => {
    if (factory) {
      factories.set(key, factory as () => AnalyticsService);
    }
  });

  const getActiveServices = (): AnalyticsService[] => {
    if (activeServices.size === 0) {
      return [NullAnalyticsService];
    }

    return Array.from(activeServices.values());
  };

  return {
    enableServices: async (names: string[]) => {
      for (const name of names) {
        if (activeServices.has(name)) continue;
        const factory = factories.get(name);
        if (!factory) {
          console.warn(
            `Analytics provider '${name}' not registered. Skipping initialization.`,
          );
          continue;
        }
        const service = factory();
        activeServices.set(name, service);
        await service.initialize();
      }
    },

    disableServices: async (names: string[]) => {
      for (const name of names) {
        const service = activeServices.get(name);
        if (!service) continue;
        if (service.shutdown) {
          await service.shutdown();
        }
        activeServices.delete(name);
      }
    },

    addProvider: (provider: T, config: Config) => {
      const factory = options.providers[provider];

      if (!factory) {
        console.warn(
          `Analytics provider '${provider}' not registered. Skipping initialization.`,
        );

        return Promise.resolve();
      }

      const service = factory(config);
      activeServices.set(provider, service);

      return service.initialize();
    },

    removeProvider: (provider: T) => {
      const service = activeServices.get(provider);
      if (service?.shutdown) {
        void service.shutdown();
      }
      activeServices.delete(provider);
    },

    identify: (userId: string, traits?: Record<string, string>) => {
      return Promise.all(
        getActiveServices().map((service) => service.identify(userId, traits)),
      );
    },

    trackPageView: (path: string) => {
      return Promise.all(
        getActiveServices().map((service) => service.trackPageView(path)),
      );
    },

    trackEvent: (
      eventName: string,
      eventProperties?: Record<string, string | string[]>,
    ) => {
      return Promise.all(
        getActiveServices().map((service) =>
          service.trackEvent(eventName, eventProperties),
        ),
      );
    },
  };
}
